<template>
  <b-modal
      id="festival-import-modal"
      centered
      size="xxl"
      no-close-on-backdrop
      footer-class="d-block"
      @close="onClose"
  >
    <!-- Header -->
    <template #modal-header>
      <div class="d-flex justify-content-between align-items-center w-100">
        <span class="h4 mb-0">Import Festivals</span>

        <div class="d-flex align-items-center">
          <b-badge variant="primary" class="mr-1">
            Step {{ step }} / 3
          </b-badge>

          <b-dropdown
              variant="link"
              no-caret
              dropleft
              :disabled="file.importing || file.loading"
              :right="isRTL"
              :toggle-class="['pr-0 btn-sm']"
          >
            <template #button-content>
              <b-icon-three-dots-vertical style="width:14px;height:14px" class="align-middle text-body" />
            </template>

            <b-dropdown-form>
              <b-dropdown-item @click="options.showPreview = !options.showPreview">
                <b-icon :icon="options.showPreview ? 'check-square' : 'square'" style="width:14px;height:14px" class="mr-50" />
                <span class="align-middle ml-50">File Preview</span>
              </b-dropdown-item>

              <template v-if="$can && $can('manage', 'dev')">
                <b-dropdown-divider />
                <b-dropdown-header>Developer</b-dropdown-header>
                <b-dropdown-item @click="options.isDebug = !options.isDebug">
                  <b-icon :icon="options.isDebug ? 'check-square' : 'square'" style="width:14px;height:14px" class="mr-50" />
                  <span class="align-middle ml-50">Debug</span>
                </b-dropdown-item>
              </template>
            </b-dropdown-form>
          </b-dropdown>
        </div>
      </div>
    </template>

    <!-- Body -->
    <template #default>
      <b-overlay :show="file.loading || file.importing" :opacity="1" rounded>
        <template #overlay>
          <b-card>
            <div class="d-flex justify-content-center mb-1">
              <b-spinner label="Loading..." variant="primary" />
            </div>
            <b-alert show variant="primary" class="mb-0">
              <div class="d-flex align-items-center mb-0">
                <b-icon-cloud-upload class="mr-50" size="16" />
                <b>{{ file.importing ? 'Importing' : 'Loading' }}</b>
              </div>
              <p class="font-small-3 mb-0">Please wait while the data is being processed.</p>
            </b-alert>
          </b-card>
        </template>

        <template #default>
          <b-container>
            <!-- Stepper -->
            <b-row v-if="showProgress" class="mb-1">
              <b-col>
                <b-progress :max="3" height="10px">
                  <b-progress-bar :value="progressValue" />
                </b-progress>
              </b-col>
            </b-row>

            <!-- STEP 1: Load -->
            <template v-if="step === 1">
              <b-row class="border rounded shadow p-50 mb-1">
                <b-col cols="12">
                  <b-form-group label="CSV File" label-for="file-name" label-class="pt-50">
                    <b-input-group>
                      <b-form-file
                          id="file-name"
                          v-model="file.value"
                          :disabled="file.loaded"
                          accept=".csv"
                          class="font-small-3"
                      />
                      <b-input-group-append>
                        <b-button variant="primary" :disabled="!file.value || file.loaded" @click="loadFile">
                          Load
                        </b-button>
                      </b-input-group-append>
                    </b-input-group>
                    <small class="text-muted font-small-3">
                      Header row is required. Expected columns are validated automatically.
                    </small>
                  </b-form-group>
                </b-col>

                <!-- Missing required headers -->
                <b-col v-if="file.loaded && hasHeaderErrors" cols="12" class="px-0">
                  <b-alert show variant="danger" class="font-small-3">
                    <div class="font-weight-bold mb-50">Missing required columns:</div>
                    <ul class="mb-0">
                      <li v-for="h in missingRequiredHeaders" :key="h">{{ h }}</li>
                    </ul>
                    <div class="mt-50">
                      Fix the header row and re-upload the file.
                    </div>
                  </b-alert>
                </b-col>
              </b-row>

              <!-- File Preview -->
              <b-row
                  v-if="options.showPreview && file.loaded && file.rawText"
                  class="border rounded shadow p-50 mb-1"
              >
                <b-col>
                  <div class="d-flex justify-content-between align-items-center">
                    <label class="mb-0 mt-50 font-weight-bold">File Preview (csv)</label>
                    <label class="mb-0 mt-50 font-weight-bold">
                      {{ previewLineCount }} line{{ previewLineCount === 1 ? '' : 's' }}
                    </label>
                  </div>

                  <vue-perfect-scrollbar :options="options.scrollbar" class="ps-export-preview">
                    <div class="csv-preview">
                      <prism language="csv">{{ preview }}</prism>
                    </div>
                  </vue-perfect-scrollbar>
                </b-col>
              </b-row>

              <!-- Continue -->
              <b-row v-if="file.loaded" class="mb-0">
                <b-col class="d-flex justify-content-end">
                  <b-button
                      variant="primary"
                      :disabled="!file.loaded || hasHeaderErrors"
                      @click="goToReview"
                  >
                    Review & Transform
                  </b-button>
                </b-col>
              </b-row>
            </template>

            <!-- STEP 2: Review -->
            <template v-else-if="step === 2">
              <b-row class="border rounded shadow p-50 mb-1">
                <b-col>
                  <div class="font-weight-bold mb-50">Created At</div>
                  <div class="font-small-3 text-muted mb-50">
                    Used for filtering by year/date ranges. This will be applied to all imported festivals.
                  </div>

                  <b-input-group>
                    <b-datepicker
                        v-model="createdAtDate"
                        :disabled="file.importing"
                        hide-header
                        nav-button-variant="primary"
                    />
                    <b-timepicker
                        v-model="createdAtTime"
                        :disabled="file.importing"
                        hide-header
                        button-variant="primary"
                        close-button-variant="primary"
                    />
                  </b-input-group>
                </b-col>
              </b-row>
              <b-row class="border rounded shadow p-50 mb-1">
                <b-col>
                  <div class="d-flex justify-content-between align-items-center">
                    <div>
                      <div class="font-weight-bold">Review Transformed Items</div>
                      <div class="font-small-3 text-muted">
                        Fix anything that looks off. Errors must be resolved before importing.
                        <div class="mt-25">
                          <b>Slug is auto-generated</b> from Name + Zone (not editable). Zone can be blank in the file — you’ll pick it here.
                        </div>
                      </div>
                    </div>

                    <div class="d-flex align-items-center">
                      <b-badge v-if="reviewErrorsCount" variant="danger" class="mr-1">
                        {{ reviewErrorsCount }} error{{ reviewErrorsCount === 1 ? '' : 's' }}
                      </b-badge>
                      <b-badge variant="primary">
                        {{ mappedItems.length }} item{{ mappedItems.length === 1 ? '' : 's' }}
                      </b-badge>
                    </div>
                  </div>

                  <hr class="mb-1" />

                  <b-alert v-if="hasDuplicateSlugs" show variant="warning" class="font-small-3">
                    Duplicate slugs detected. A festival cannot have the same Name + Zone combination.
                  </b-alert>

                  <vue-perfect-scrollbar :options="options.scrollbar" class="ps-review-table border rounded">
                    <b-table
                        small
                        bordered
                        striped
                        responsive
                        :items="mappedItems"
                        :fields="reviewTableFields"
                        primary-key="id"
                        class="mb-0"
                    >
                      <template #cell(name)="slot">
                        <b-form-input
                            v-model.trim="slot.item.name"
                            size="sm"
                            :state="fieldState(slot.item, 'name')"
                            @input="onNameChanged(slot.item)"
                        />
                        <small v-if="fieldError(slot.item, 'name')" class="text-danger">
                          {{ fieldError(slot.item, 'name') }}
                        </small>
                      </template>

                      <!-- Zone select -->
                      <template #cell(festivalZoneId)="slot">
                        <v-select
                            v-model="slot.item.festivalZoneId"
                            :options="zoneOptions"
                            :reduce="reduceZoneId"
                            label="name"
                            :clearable="true"
                            :dir="isRTL ? 'rtl' : 'ltr'"
                            class="font-small-3"
                            append-to-body
                            :calculate-position="calculateVSelectPosition"
                            @input="onZoneChanged(slot.item)"
                        />
                        <small v-if="fieldError(slot.item, 'festivalZoneId')" class="text-danger">
                          {{ fieldError(slot.item, 'festivalZoneId') }}
                        </small>
                      </template>

                      <template #cell(site)="slot">
                        <b-form-input v-model.trim="slot.item.site" size="sm" />
                      </template>

                      <!-- Address fields -->
                      <template #cell(addressLine1)="slot">
                        <b-form-input v-model.trim="slot.item.address.line1" size="sm" />
                      </template>

                      <template #cell(addressLine2)="slot">
                        <b-form-input v-model.trim="slot.item.address.line2" size="sm" />
                      </template>

                      <template #cell(addressCity)="slot">
                        <b-form-input v-model.trim="slot.item.address.city" size="sm" />
                      </template>

                      <template #cell(addressCounty)="slot">
                        <b-form-select
                            v-model="slot.item.address.county"
                            size="sm"
                            :options="countyOptionsFor(slot.item)"
                            :disabled="!slot.item.festivalZoneId"
                        />
                      </template>

                      <template #cell(addressState)="slot">
                        <b-form-input v-model.trim="slot.item.address.state" size="sm" />
                      </template>

                      <template #cell(addressZip)="slot">
                        <b-form-input v-model.trim="slot.item.address.zip" size="sm" />
                      </template>

                      <template #cell(enabled)="slot">
                        <b-form-checkbox v-model="slot.item.enabled" switch />
                      </template>

                      <template #cell(_rowErrors)="slot">
                        <div v-if="rowErrors(slot.item).length">
                          <b-badge variant="danger">Error</b-badge>
                          <div class="font-small-3 text-danger mt-50">
                            <div v-for="(e, idx) in rowErrors(slot.item)" :key="idx">{{ e }}</div>
                          </div>
                        </div>
                        <b-badge v-else variant="success">OK</b-badge>
                      </template>

                      <!-- NEW: Actions (remove row) -->
                      <template #cell(_actions)="slot">
                        <b-button
                            size="sm"
                            variant="outline-danger"
                            :disabled="file.importing || mappedItems.length <= 1"
                            title="Remove this row"
                            @click="removeRow(slot.item)"
                        >
                          Remove
                        </b-button>
                      </template>
                    </b-table>
                  </vue-perfect-scrollbar>

                  <div class="d-flex justify-content-between mt-1">
                    <b-button variant="outline-secondary" @click="step = 1">Back</b-button>

                    <div class="d-flex align-items-center">
                      <b-button variant="outline-primary" class="mr-1" @click="addBlankRow">
                        + Add Blank Row
                      </b-button>

                      <b-button variant="outline-danger" class="mr-1" @click="resetReview">
                        Reset
                      </b-button>

                      <b-button variant="primary" :disabled="!canImport" @click="step = 3">
                        Continue to Import
                      </b-button>
                    </div>
                  </div>

                  <small v-if="mappedItems.length <= 1" class="text-muted font-small-3 d-block mt-50">
                    Note: You can’t remove the last remaining row.
                  </small>
                </b-col>
              </b-row>

              <b-row v-if="options.isDebug" class="border rounded shadow p-50">
                <b-col>
                  <div class="font-weight-bold mb-50">Debug: mappedItems</div>
                  <pre class="mb-0 font-small-3">{{ mappedItems }}</pre>
                </b-col>
              </b-row>
            </template>

            <!-- STEP 3: Import -->
            <template v-else>
              <b-row class="border rounded shadow p-50 mb-1">
                <b-col>
                  <div class="font-weight-bold">Ready to Import</div>
                  <div class="font-small-3 text-muted">
                    This will create festivals using the final transformed payload.
                  </div>

                  <hr class="mb-1" />

                  <b-alert show variant="primary" class="font-small-3">
                    Importing: <b>{{ mappedItems.length }}</b> festival{{ mappedItems.length === 1 ? '' : 's' }}.
                  </b-alert>

                  <div class="d-flex justify-content-between">
                    <b-button variant="outline-secondary" @click="step = 2">Back</b-button>
                    <b-button variant="primary" :disabled="!canImport" @click="importTransformed">Import</b-button>
                  </div>
                </b-col>
              </b-row>

              <b-row v-if="options.isDebug" class="border rounded shadow p-50">
                <b-col>
                  <div class="font-weight-bold mb-50">Debug: payload</div>
                  <pre class="mb-0 font-small-3">{{ importPayload }}</pre>
                </b-col>
              </b-row>
            </template>
          </b-container>
        </template>
      </b-overlay>
    </template>

    <!-- Footer -->
    <template #modal-footer>
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <b-button v-if="file.loaded || mappedItems.length" variant="danger" :disabled="file.importing" @click="clear">
            Clear
          </b-button>
        </div>

        <div>
          <b-button class="mr-1" :disabled="file.importing" @click="onClose">
            Close
          </b-button>

          <b-button
              v-if="step === 3"
              variant="primary"
              :disabled="file.importing || !canImport"
              @click="importTransformed"
          >
            Import
          </b-button>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import _ from 'lodash'
import { flatten, unflatten } from 'flat'
import vSelect from 'vue-select'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import Prism from 'vue-prism-component'
import 'prismjs/components/prism-csv.min'

import slugify from 'slugify'
import storageLocal from '@/mixins/storage.local.mixin'
import { uuid } from 'vue-uuid'
import { API, graphqlOperation } from 'aws-amplify'
import { createFestival } from '@/graphql/queries/festival-modal'

export default {
  name: 'FestivalImportModal',
  components: { Prism, vSelect, VuePerfectScrollbar },
  mixins: [storageLocal],

  props: {
    expectedFields: { type: Array, required: false, default: () => [] },
    zones: { type: Array, required: false, default: () => [] }, // [{ id, name, counties }]
  },

  data() {
    return {
      modal: 'festival-import-modal',
      step: 1,

      file: {
        value: null,
        headers: [],
        items: [],
        rawText: '',
        loading: false,
        loaded: false,
        importing: false,
      },

      createdAtDate: null,
      createdAtTime: null,

      mappedItems: [],

      options: {
        showPreview: false,
        previewLimit: 30,
        scrollbar: { maxScrollbarLength: 150, wheelPropagation: false, suppressScrollX: true },
        isDebug: false,
      },
    }
  },
  created() {
    this.setCreatedAtDefaults()
  },
  computed: {
    isRTL() {
      return !!(this.$store?.state?.appConfig?.isRTL)
    },

    showProgress() {
      return !!(this.file.value || this.file.loaded)
    },

    progressValue() {
      if (!this.file.loaded && this.step === 1) return 0
      return this.step
    },

    zoneOptions() {
      return Array.isArray(this.zones) ? this.zones : []
    },

    formattedCreatedAt() {
      if (!this.createdAtDate) return new Date().toISOString()

      const t = String(this.createdAtTime || '00:00').trim()
      const time = t.length === 5 ? `${t}:00` : t // HH:mm -> HH:mm:ss

      const d = new Date(`${this.createdAtDate}T${time}`) // local -> ISO Z
      d.setMilliseconds(999)
      return d.toISOString()
    },

    previewLineCount() {
      if (!this.file.rawText) return 0
      const lines = this.file.rawText.split(/\r?\n/)
      return Math.min(lines.length, this.options.previewLimit)
    },

    preview() {
      if (!this.file.rawText) return ''
      const lines = this.file.rawText.split(/\r?\n/)
      return lines.slice(0, this.options.previewLimit).join('\n')
    },

    normalizedHeaders() {
      return (this.file.headers || []).map(h => String(h).trim().toLowerCase())
    },

    missingRequiredHeaders() {
      const expected = (this.expectedFields || []).map(h => String(h).trim().toLowerCase()).filter(Boolean)
      if (!expected.length) return []
      return expected.filter(h => !this.normalizedHeaders.includes(h))
    },

    hasHeaderErrors() {
      return this.missingRequiredHeaders.length > 0
    },

    reviewTableFields() {
      return [
        { key: 'name', label: 'Name', thStyle: { minWidth: '200px' } },
        { key: 'festivalZoneId', label: 'Zone', thStyle: { minWidth: '180px' } },
        { key: 'site', label: 'Site', thStyle: { minWidth: '180px' } },

        { key: 'addressLine1', label: 'Line 1', thStyle: { minWidth: '200px' } },
        { key: 'addressLine2', label: 'Line 2', thStyle: { minWidth: '200px' } },
        { key: 'addressCity', label: 'City', thStyle: { minWidth: '160px' } },
        { key: 'addressCounty', label: 'County', thStyle: { minWidth: '180px' } },
        { key: 'addressState', label: 'State', thStyle: { minWidth: '90px' } },
        { key: 'addressZip', label: 'Zip', thStyle: { minWidth: '120px' } },

        { key: 'enabled', label: 'Enabled', thStyle: { width: '110px' } },
        { key: '_rowErrors', label: 'Status', thStyle: { width: '160px' } },

        // NEW
        { key: '_actions', label: '', thStyle: { width: '120px' } },
      ]
    },

    hasDuplicateSlugs() {
      const slugs = this.mappedItems.map(x => (x.slug || '').trim()).filter(Boolean)
      return slugs.length !== new Set(slugs).size
    },

    reviewErrorsCount() {
      return this.mappedItems.reduce((acc, item) => acc + this.rowErrors(item).length, 0)
    },

    canImport() {
      return this.mappedItems.length > 0 && this.reviewErrorsCount === 0 && !this.hasDuplicateSlugs
    },

    importPayload() {
      return this.mappedItems.map(item => ({
        id: item.id,
        slug: item.slug,
        name: item.name,
        festivalZoneId: item.festivalZoneId || null,
        site: item.site || '',
        address: {
          line1: item.address?.line1 || '',
          line2: item.address?.line2 || '',
          city: item.address?.city || '',
          county: item.address?.county || '',
          state: item.address?.state || '',
          zip: item.address?.zip || '',
        },
        state: { enabled: item.enabled !== false },
        createdAt: this.formattedCreatedAt,
      }))
    },
  },

  methods: {
    reduceZoneId(zone) {
      return zone && zone.id ? zone.id : null
    },

    setCreatedAtDefaults() {
      const now = new Date()
      const pad = (n) => String(n).padStart(2, '0')
      const yyyy = now.getFullYear()
      const mm = pad(now.getMonth() + 1)
      const dd = pad(now.getDate())
      const hh = pad(now.getHours())
      const mi = pad(now.getMinutes())
      const ss = pad(now.getSeconds())

      this.createdAtDate = `${yyyy}-${mm}-${dd}`
      this.createdAtTime = `${hh}:${mi}:${ss}`
    },

    calculateVSelectPosition(dropdownList, component, { width }) {
      const getToggleEl = () =>
          component?.$el?.querySelector('.vs__dropdown-toggle') || component?.$el

      const setPos = () => {
        const toggle = getToggleEl()
        if (!toggle) return
        const rect = toggle.getBoundingClientRect()

        dropdownList.style.position = 'fixed'
        dropdownList.style.top = `${rect.bottom}px`
        dropdownList.style.left = `${rect.left}px`
        dropdownList.style.width = width || `${rect.width}px`
        dropdownList.style.zIndex = '1060'
      }

      const rafSetPos = () => requestAnimationFrame(setPos)

      setPos()

      const modalEl = document.getElementById('festival-import-modal')
      const psEl = modalEl?.querySelector('.ps-review-table')
      const modalDialog = modalEl?.querySelector('.modal-dialog')

      window.addEventListener('resize', rafSetPos)
      window.addEventListener('scroll', rafSetPos, true)
      // eslint-disable-next-line no-unused-expressions
      psEl?.addEventListener('scroll', rafSetPos)
      // eslint-disable-next-line no-unused-expressions
      modalDialog?.addEventListener('scroll', rafSetPos)

      return () => {
        window.removeEventListener('resize', rafSetPos)
        window.removeEventListener('scroll', rafSetPos, true)
        // eslint-disable-next-line no-unused-expressions
        psEl?.removeEventListener('scroll', rafSetPos)
        // eslint-disable-next-line no-unused-expressions
        modalDialog?.removeEventListener('scroll', rafSetPos)
      }
    },

    findZoneById(id) {
      if (!id) return null
      const zones = this.zoneOptions
      for (let i = 0; i < zones.length; i++) {
        if (String(zones[i].id) === String(id)) return zones[i]
      }
      return null
    },

    buildSlug(name, zoneId) {
      const n = (name || '').trim()
      const zone = this.findZoneById(zoneId)
      const zName = zone?.name ? zone.name : ''
      return slugify(`f${n}z${zName}`, { lower: true })
    },

    countyOptionsFor(item) {
      const zone = this.findZoneById(item ? item.festivalZoneId : null)
      const counties = zone && Array.isArray(zone.counties) ? zone.counties : []
      const opts = [{ value: '', text: '— Select —' }]
      for (let i = 0; i < counties.length; i++) opts.push({ value: counties[i], text: counties[i] })
      return opts
    },

    onClose() {
      this.$bvModal.hide(this.modal)
      this.clear()
    },

    clear() {
      this.step = 1
      this.file.value = null
      this.file.headers = []
      this.file.items = []
      this.file.rawText = ''
      this.file.loaded = false
      this.file.loading = false
      this.file.importing = false
      this.mappedItems = []
      this.setCreatedAtDefaults()
    },

    parseCsvLine(line) {
      const out = []
      let cur = ''
      let inQuotes = false

      for (let i = 0; i < line.length; i++) {
        const ch = line[i]

        if (inQuotes) {
          if (ch === '"') {
            if (line[i + 1] === '"') {
              cur += '"'
              i++
            } else {
              inQuotes = false
            }
          } else {
            cur += ch
          }
        }
        else if (ch === '"') {
            inQuotes = true
          }
        else if (ch === ',') {
            out.push(cur)
            cur = ''
          }
        else {
            cur += ch
          }
      }

      out.push(cur)
      return out.map(v => String(v ?? '').trim())
    },

    parseCSV(data) {
      const rows = String(data)
          .split(/\r?\n/)
          .filter(r => r.trim().length)

      if (!rows.length) return { headers: [], items: [] }

      const headerRow = rows.shift()
      const headers = this.parseCsvLine(headerRow).map(h => h.trim())

      const items = rows.map(row => {
        const values = this.parseCsvLine(row)
        return headers.reduce((acc, header, index) => {
          acc[header] = values[index] ?? ''
          return acc
        }, {})
      })

      return { headers, items }
    },

    loadFile() {
      const { value } = this.file
      if (!value) return

      const name = String(value.name || '').toLowerCase()
      if (!name.endsWith('.csv')) {
        this.file.loaded = false
        this.file.items = []
        this.file.headers = []
        this.file.rawText = ''
        // eslint-disable-next-line no-alert
        alert('Please upload a .csv file.')
        return
      }

      this.file.loading = true

      const reader = new FileReader()
      reader.onload = e => {
        const data = String(e?.target?.result ?? '')
        this.file.rawText = data

        try {
          const parsed = this.parseCSV(data)
          this.file.items = parsed.items || []

          const first = this.file.items?.[0] || null
          this.file.headers = first ? Object.getOwnPropertyNames(flatten(first, { safe: true })) : parsed.headers || []

          this.file.loaded = true
        } catch (err) {
          console.error(err)
          this.file.items = []
          this.file.headers = []
          this.file.rawText = ''
          this.file.loaded = false
        } finally {
          this.file.loading = false
        }
      }

      reader.readAsText(this.file.value)
    },

    goToReview() {
      const rawItems = (this.file.items || []).map(x => unflatten(x))
      this.mappedItems = this.transformItems(rawItems)
      this.step = 2
    },

    resetReview() {
      this.goToReview()
    },

    transformItems(items) {
      const out = []
      const list = items || []

      for (let i = 0; i < list.length; i++) {
        const item = list[i] || {}
        const addr = item.address || {}

        // eslint-disable-next-line no-nested-ternary
        const zoneId = (item.zone && item.zone.id) ? item.zone.id : (item.festivalZoneId ? item.festivalZoneId : null)

        const name = item.name || ''
        const slug = this.buildSlug(name, zoneId)

        out.push({
          id: uuid.v4(),
          name,
          festivalZoneId: zoneId,
          slug,
          site: item.site || '',
          address: {
            line1: addr.line1 || item.line1 || item.addressLine1 || '',
            line2: addr.line2 || item.line2 || item.addressLine2 || '',
            city: addr.city || item.city || item.addressCity || '',
            county: addr.county || item.county || item.addressCounty || '',
            state: addr.state || item.state || item.addressState || '',
            zip: addr.zip || item.zip || item.addressZip || '',
          },
          enabled: item.enabled !== undefined ? item.enabled : true,
        })
      }

      for (let j = 0; j < out.length; j++) this.ensureCountyValid(out[j])
      return out
    },

    ensureCountyValid(item) {
      if (!item?.address) return
      const zone = this.findZoneById(item.festivalZoneId)
      const counties = zone && Array.isArray(zone.counties) ? zone.counties : []
      const current = (item.address.county || '').trim()
      if (!current) return
      if (counties.length && counties.indexOf(current) === -1) item.address.county = ''
    },

    onNameChanged(item) {
      if (!item) return
      item.slug = this.buildSlug(item.name, item.festivalZoneId)
    },

    onZoneChanged(item) {
      if (!item) return
      item.slug = this.buildSlug(item.name, item.festivalZoneId)
      this.ensureCountyValid(item)
    },

    addBlankRow() {
      const item = {
        id: uuid.v4(),
        name: '',
        festivalZoneId: null,
        slug: this.buildSlug('', null),
        site: '',
        address: {
          line1: '',
          line2: '',
          city: '',
          county: '',
          state: '',
          zip: '',
        },
        enabled: true,
      }
      this.mappedItems.push(item)
    },

    // NEW: remove row
    removeRow(item) {
      if (!item) return
      if (this.mappedItems.length <= 1) return
      const idx = this.mappedItems.findIndex(x => x && x.id === item.id)
      if (idx >= 0) this.mappedItems.splice(idx, 1)
    },

    rowErrors(item) {
      const errors = []
      const name = (item.name || '').trim()
      const slug = (item.slug || '').trim()

      if (!name) errors.push('Name is required.')
      if (!item.festivalZoneId) errors.push('Zone is required.')
      if (!slug) errors.push('Slug is required.')

      if (slug && !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) {
        errors.push('Slug must be lowercase kebab-case (a-z, 0-9, hyphen).')
      }

      if (this.zones?.length && item.festivalZoneId) {
        if (!this.findZoneById(item.festivalZoneId)) errors.push('Zone does not match a known zone.')
      }

      const zone = this.findZoneById(item.festivalZoneId)
      const counties = zone && Array.isArray(zone.counties) ? zone.counties : []
      const county = item.address?.county ? String(item.address.county).trim() : ''
      if (county && counties.length && counties.indexOf(county) === -1) {
        errors.push('County is not valid for the selected zone.')
      }

      return errors
    },

    fieldError(item, field) {
      const errs = this.rowErrors(item)
      if (!errs.length) return null
      if (field === 'name') return (item.name || '').trim() ? null : 'Required'
      if (field === 'festivalZoneId') return item.festivalZoneId ? null : 'Required'
      return null
    },

    fieldState(item, field) {
      const err = this.fieldError(item, field)
      if (err) return false
      if (field === 'name') return (item.name || '').trim() ? true : null
      if (field === 'festivalZoneId') return item.festivalZoneId ? true : null
      return null
    },

    async importTransformed() {
      if (!this.canImport) return
      this.file.importing = true

      try {
        await this.importPayload.reduce(async (p, payload) => {
          await p
          const response = await API.graphql(graphqlOperation(createFestival, { input: payload }))
          const festival = response.data.createFestival
          this.$emit('imported', festival)
        }, Promise.resolve())
      } catch (err) {
        console.error(err)
      } finally {
        this.file.importing = false
        this.onClose()
      }
    },
  },
}
</script>

<style scoped>
.ps-export-preview {
  max-height: 30vh;
}
.ps-review-table {
  max-height: 55vh;
}
.csv-preview > pre[class*='language-'] {
  background: #fff;
  border: 1px dashed #ebe9f1;
  border-radius: 0.5rem;
  padding: 1rem !important;
}
.csv-preview > pre[class*='language-'] > code {
  padding: 0 !important;
}
</style>

<style lang="scss">

#festival-import-modal .modal-dialog.modal-xxl {
  max-width: 75vw; /* 2560x1440 (and up) */
}

/* For smaller screens (ex: 1920x1080), make it a bit wider */
@media (max-width: 2500px) {
  #festival-import-modal .modal-dialog.modal-xxl {
    max-width: 95vw;
  }
}
.vs__dropdown-menu {
  z-index: 1060;
}
</style>
